/**
 * Newsletter Pop-in — Industrialized Implementation
 * Replaces A/B test 1557984 (Newsletter Pop-in left vs Newsletter Pop-in footer)
 *
 * Winning variation (Variation 1): Pop-in on the left → default experience
 * Personalization fallback (Variation 2): Sticky footer bar → shown after 30 days
 *   if user closed Variation 1
 *
 * Priority rules:
 *   Rule 1 → User never saw the pop-in               → show Variation 1 (left pop-in)
 *   Rule 2 → User closed Variation 1                 → suppress everything for 30 days,
 *                                                        then show Variation 2
 *   Rule 3 → User closed Variation 2                 → V2 stays default fallback,
 *                                                        V1 never re-enters
 *
 * Trigger guards:
 *   V1 → ≥ N seconds on page (data-delay / 1000) AND ≥ N pages viewed (data-pageviews)
 *        Both values are sourced from Storyblok via data attributes on <newsletter-popin>.
 *        Fallback defaults: 10 seconds, 2 page views.
 *   V2 → scroll ≥ 33% of page → show bar; scroll back below 33% → hide bar
 *
 * Content / design / page exclusions → managed via Storyblok (source of truth)
 * State persistence                  → localStorage
 */
(function () {
  'use strict';

  // ─── Constants ────────────────────────────────────────────────────────────

  var LS_KEY_V1_CLOSED_AT = 'nl_popin_v1_closed_at';
  var LS_KEY_V2_ELIGIBLE  = 'nl_popin_v2_eligible';
  var LS_KEY_V2_CLOSED    = 'nl_popin_v2_closed';
  var LS_KEY_SUBSCRIBED   = 'newsletterSubscribed';
  
  // Logic to handle "next session" requirement
  var SS_KEY_V1_CLOSED_THIS_SESSION = 'nl_popin_v1_closed_session';

  var V1_SUPPRESS_MS   = 30 * 24 * 60 * 60 * 1000; // 30 days
  var TRIGGER_SCROLL_PCT = 33;

  // ─── Trigger config — resolved lazily in initV1 once element is in DOM ────
  // Fallback defaults used until resolveTriggerConfig() runs.

  var TRIGGER_MIN_SECONDS   = 10;
  var TRIGGER_MIN_PAGEVIEWS = 2;

  function resolveTriggerConfig() {
    var el = document.querySelector('.c-newsletter-popin-tasty-a');
    if (!el) return;

    var delay     = parseInt(el.getAttribute('data-delay')     || '10000', 10);
    var pageviews = parseInt(el.getAttribute('data-pageviews') || '2',     10);

    var daysAttr  = el.getAttribute('data-numberDays');
    var days      = parseInt(daysAttr, 10) || 30; // Fallback to 30 if empty
    console.log("daysAttr", daysAttr);

    TRIGGER_MIN_SECONDS   = Math.max(0, delay) / 1000;
    TRIGGER_MIN_PAGEVIEWS = Math.max(1, pageviews);
    V1_SUPPRESS_MS = days * 24 * 60 * 60 * 1000;

    console.log('[Newsletter] Trigger config resolved from DOM', {
      TRIGGER_MIN_SECONDS:   TRIGGER_MIN_SECONDS,
      TRIGGER_MIN_PAGEVIEWS: TRIGGER_MIN_PAGEVIEWS,
      V1_SUPPRESS_DAYS:      days
    });
  }

  // ─── Storage helpers ──────────────────────────────────────────────────────

  function lsGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function lsSet(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { }
  }

  function lsClear(key) {
    try { localStorage.removeItem(key); } catch (e) { }
  }

  // ─── Page-view counter ────────────────────────────────────────────────────

  function getPageViews() {
    try { return parseInt(sessionStorage.getItem('page_viewed') || '0', 10); } catch (e) { return 0; }
  }

  // ─── Eligibility logic ────────────────────────────────────────────────────

  function resolveEligibleVariant() {
    var v1ClosedAt = lsGet(LS_KEY_V1_CLOSED_AT);
    var v2Eligible = lsGet(LS_KEY_V2_ELIGIBLE);
    var v2Closed   = lsGet(LS_KEY_V2_CLOSED);
    
    // Check if V1 was closed during this specific browser session
    var closedInCurrentSession = false;
    try { 
      closedInCurrentSession = sessionStorage.getItem(SS_KEY_V1_CLOSED_THIS_SESSION) === 'true'; 
    } catch (e) {}

    console.log('[Newsletter] Resolving variant', { v1ClosedAt: v1ClosedAt, v2Eligible: v2Eligible, v2Closed: v2Closed, closedInCurrentSession: closedInCurrentSession });

    // Rule 0: already subscribed → never show again
    if (lsGet(LS_KEY_SUBSCRIBED) === 'true') {
      console.log('[Newsletter] Rule 0 → Already subscribed → show nothing');
      return null;
    }

    // Rule 2: V1 was closed
    if (v1ClosedAt) {
      // ADDED: If it was closed in THIS session, we show nothing (wait for next session)
      if (closedInCurrentSession) {
        console.log('[Newsletter] V1 closed in this session → Suppressing V2 until next session');
        return null;
      }

      var closedAtMs = parseInt(v1ClosedAt, 10);

      if (isNaN(closedAtMs)) {
        console.warn('[Newsletter] Corrupt v1ClosedAt — resetting');
        lsClear(LS_KEY_V1_CLOSED_AT);
        lsClear(LS_KEY_V2_ELIGIBLE);
        lsClear(LS_KEY_V2_CLOSED);
        return 'v1';
      }

      var age = Date.now() - closedAtMs;
      console.log('[Newsletter] Rule 2 → V1 was closed', { ageMs: age });

      // Reset after 30 days → back to V1
      if (age >= V1_SUPPRESS_MS) {
        console.log('[Newsletter] 30 days elapsed → resetting cycle → V1');
        lsClear(LS_KEY_V1_CLOSED_AT);
        lsClear(LS_KEY_V2_ELIGIBLE);
        lsClear(LS_KEY_V2_CLOSED);
        return 'v1';
      }

      // Within 30 days → show V2
      console.log('[Newsletter] Within 30 days → showing V2 fallback');

      // Rule 3: if V2 was closed → still V2 (permanent fallback)
      if (v2Closed === 'true') {
        console.log('[Newsletter] Rule 3 → V2 already closed → keep V2');
      }

      return 'v2';
    }

    // Rule 1: first-time user → show V1
    console.log('[Newsletter] Rule 1 → First time user → V1');
    return 'v1';
  }

  // ─── Trigger guard — V1 ───────────────────────────────────────────────────
  // Time (seconds) AND page views must both be met before callback fires.

  function waitForV1Triggers(callback) {
    var triggered    = false;
    var timeElapsed  = false;

    function tryTrigger() {
      if (triggered) return;
      var pageViewsMet = getPageViews() >= TRIGGER_MIN_PAGEVIEWS;
      console.log('[Newsletter] V1 tryTrigger', { timeElapsed: timeElapsed, pageViewsMet: pageViewsMet });
      if (timeElapsed && pageViewsMet) {
        triggered = true;
        clearInterval(pageViewPoller);
        console.log('[Newsletter] V1 triggers met → showing pop-in');
        callback();
      }
    }

    // Poll every 500 ms so a page-view increment is noticed quickly
    var pageViewPoller = setInterval(tryTrigger, 500);

    setTimeout(function () {
      timeElapsed = true;
      console.log('[Newsletter] V1 time trigger reached (' + TRIGGER_MIN_SECONDS + ' s)');
      tryTrigger();
    }, TRIGGER_MIN_SECONDS * 1000);

    console.log('[Newsletter] V1 waiting for triggers', {
      pageViews: getPageViews(),
      needed:    TRIGGER_MIN_PAGEVIEWS
    });
  }

  // ─── Trigger guard — V2 ───────────────────────────────────────────────────
  // Bar is shown when scroll ≥ 33% and hidden again when scroll drops below 33%.
  // The scroll listener stays active for the lifetime of the page.

  function waitForV2Triggers(callback) {
    var shown = false;

    function onScroll() {
      var scrollTop = window.scrollY;
      var docHeight = document.documentElement.scrollHeight - window.innerHeight;
      var scrollPct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

      var bar = document.querySelector('.newsletter-bar');

      if (scrollPct >= TRIGGER_SCROLL_PCT) {
        if (!shown) {
          // First time crossing the threshold → let the callback handle the initial reveal
          shown = true;
          console.log('[Newsletter] V2 scroll trigger reached', { scrollPct: scrollPct.toFixed(1) });
          callback();
        } else if (bar) {
          // Re-entering the zone after having scrolled back up → restore visibility
          bar.classList.remove('hide-block');
          bar.setAttribute('aria-hidden', 'false');
        }
      } else {
        // Below threshold → hide the bar (only once it has been shown at least once)
        if (shown && bar) {
          bar.classList.add('hide-block');
          bar.setAttribute('aria-hidden', 'true');
          console.log('[Newsletter] V2 scroll below threshold → hiding bar', { scrollPct: scrollPct.toFixed(1) });
        }
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    console.log('[Newsletter] V2 waiting for scroll ≥', TRIGGER_SCROLL_PCT, '%');
  }

  // ─── Variation 1 ─────────────────────────────────────────────────────────

  function initV1() {
    console.log('[Newsletter] Init V1 (left pop-in)');

    var popin = document.querySelector('.c-newsletter-popin-tasty-a');
    if (!popin) {
      console.warn('[Newsletter] V1 pop-in element not found in DOM — aborting');
      return;
    }

    // Read data-delay / data-pageviews now that the element is confirmed in DOM
    resolveTriggerConfig();

    hideV2();
    popin.style.setProperty('display', 'none', 'important');

    waitForV1Triggers(function () {
      console.log('[Newsletter] V1 triggers met → showing pop-in');

      popin.style.removeProperty('display');
      popin.classList.remove('hide-block');
      popin.classList.remove('display-none');
      popin.classList.add('is-visible');
      popin.setAttribute('aria-hidden', 'false');

      popin.querySelectorAll('.c-newsletter-popin__header button').forEach(function (btn) {
        btn.addEventListener('click', closeV1);
      });

      var form = popin.querySelector('#Newsletter-block_newsletter_form');
      if (form) {
        form.addEventListener('submit', function () {
          form.dataset.submitted = 'true';
        });

        popin.querySelectorAll('#Newsletter-block_newsletter_form button[type="submit"]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            setTimeout(function () {
              if (form.dataset.submitted === 'true') {
                onSubscribeSuccess('v1');
              }
            }, 300);
          });
        });
      }
    });
  }

  var v1CloseGuard = false;
  function closeV1() {
    if (v1CloseGuard) return;
    v1CloseGuard = true;
    console.log('[Newsletter] V1 closed → V2 will be eligible in next session');

    var popin = document.querySelector('.c-newsletter-popin-tasty-a');
    if (popin) {
      popin.classList.remove('is-visible');
      popin.style.setProperty('display', 'none', 'important');
      popin.setAttribute('aria-hidden', 'true');
    }

    lsSet(LS_KEY_V1_CLOSED_AT, String(Date.now()));
    lsSet(LS_KEY_V2_ELIGIBLE, 'true');
    
    // Flag this session so V2 doesn't show immediately
    try { sessionStorage.setItem(SS_KEY_V1_CLOSED_THIS_SESSION, 'true'); } catch (e) {}
  }

  // ─── Variation 2 ─────────────────────────────────────────────────────────

  function initV2() {
    console.log('[Newsletter] Init V2 (footer bar)');

    hideV1();

    var bar = document.querySelector('.newsletter-bar');
    if (!bar) {
      console.warn('[Newsletter] V2 bar element not found in DOM');
      return;
    }

    waitForV2Triggers(function () {
      console.log('[Newsletter] Showing V2 bar');
      bar.classList.remove('hide-block');
      bar.setAttribute('aria-hidden', 'false');
    });

    initBarClick();
  }

  var v2CloseGuard = false;
  function closeV2() {
    if (v2CloseGuard) return;
    v2CloseGuard = true;
    console.log('[Newsletter] Closing V2 → Rule 3 activated (permanent fallback)');

    var bar = document.querySelector('.newsletter-bar');
    if (bar) {
      bar.classList.add('hide-block');
      bar.setAttribute('aria-hidden', 'true');
    }

    lsSet(LS_KEY_V2_CLOSED, 'true');
  }

  // ─── Subscription success ─────────────────────────────────────────────────

  function onSubscribeSuccess(source) {
    console.log('[Newsletter] Subscription confirmed from', source, '→ suppressing forever');
    lsSet(LS_KEY_SUBSCRIBED, 'true');

    var popin = document.querySelector('.c-newsletter-popin-tasty-a');
    if (popin) {
      popin.classList.remove('is-visible');
      popin.style.setProperty('display', 'none', 'important');
    }

    var bar = document.querySelector('.newsletter-bar');
    if (bar) {
      bar.classList.add('hide-block');
    }
  }

  // ─── Tracking ─────────────────────────────────────────────────────────────

  function trackCtaClick(position) {
    console.log('[Newsletter] CTA clicked — position:', position);
  }

  // ─── Bootstrap ────────────────────────────────────────────────────────────

  function boot() {
    console.log('[Newsletter] Booting script');

    resolveTriggerConfig();
    var variant = resolveEligibleVariant();
    console.log('[Newsletter] Selected variant:', variant);

    if (!variant) {
      console.log('[Newsletter] No variant to display');
      return;
    }

    if (variant === 'v1') {
      initV1();
    } else if (variant === 'v2') {
      initV2();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // ─── Form submit tracking ─────────────────────────────────────────────────

  document.addEventListener('submit', async function (e) {
    var form = e.target;
    if (!form.closest('.side-bar-newsletter')) return;

    try {
      var emailInput     = form.querySelector('input[name="contact[email]"]');
      var firstNameInput = form.querySelector('input[name="contact[first_name]"]');
      var lastNameInput  = form.querySelector('input[name="contact[last_name]"]');

      if (!emailInput) return;

      var email     = emailInput.value || '';
      var firstName = firstNameInput ? firstNameInput.value : '';
      var lastName  = lastNameInput  ? lastNameInput.value  : '';

      var buffer      = new TextEncoder().encode(email.trim().toLowerCase());
      var hash        = await crypto.subtle.digest('SHA-256', buffer);
      var hashedEmail = Array.from(new Uint8Array(hash))
        .map(function (b) { return b.toString(16).padStart(2, '0'); })
        .join('');

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event:       'newsletter_submit',
        email_sha256: hashedEmail,
        first_name:   firstName,
        last_name:    lastName,
        source:       'sidebar_newsletter'
      });

      onSubscribeSuccess('sidebar');

      console.log('[Newsletter] dataLayer push OK');

    } catch (err) {
      console.error('[Newsletter] Tracking error', err);
    }
  });

  // ─── Hide helpers ─────────────────────────────────────────────────────────

  function hideV1() {
    var popin = document.querySelector('.c-newsletter-popin-tasty-a');
    if (popin) {
      popin.classList.remove('is-visible');
      popin.classList.add('hide-block');
      popin.style.setProperty('display', 'none', 'important');
      popin.setAttribute('aria-hidden', 'true');
    }
  }

  function hideV2() {
    var bar = document.querySelector('.newsletter-bar');
    if (bar) {
      bar.classList.add('hide-block');
      bar.style.setProperty('display', 'none', 'important');
      bar.setAttribute('aria-hidden', 'true');
    }
  }

  // ─── Bar click → open sidebar panel ──────────────────────────────────────

  function initBarClick() {
    var bar = document.querySelector('.newsletter-bar');
    if (!bar) return;

    bar.addEventListener('click', function (e) {
      e.preventDefault();

      var sidebarId = bar.getAttribute('data-sidebar-open');
      if (!sidebarId) return;

      var panel = document.getElementById(sidebarId);
      if (panel) {
        panel.style.removeProperty('display');
        panel.classList.remove('hide-block');
        panel.setAttribute('aria-hidden', 'false');
        panel.setAttribute('data-state', 'open');
      }

      var toggle = bar.querySelector('[aria-expanded]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    });
  }

})();